import { Injectable } from '@angular/core'
import { Http, Response, URLSearchParams, Headers } from '@angular/http'
import { AngularFireDatabase } from 'angularfire2/database'

import { Observable } from 'rxjs/Observable'

import { Movie, Theatre } from './movie.model'
import { Trailer } from './trailer.model'

import { environment } from '../../../environments/environment'

import * as moment from 'moment'
import * as firebase from 'firebase'

@Injectable()
export class MovieService {
    private data: Movie[]
    private observable: Observable<Movie[]>

    private readonly firebaseRef = firebase.database().ref(environment.firebaseRoot)

    private headers: Headers

    constructor(private db: AngularFireDatabase,
      private http: Http) {
        this.headers = new Headers({
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        })
    }

    public getMovies(lang?: string, forceReload = false): Observable<Movie[]> {
        if (forceReload || !this.observable) {
            const params = new URLSearchParams()
            params.set('startDate', moment().format())
            params.set('numDays', '7')
            params.set('lat', '45.55799439258627')
            params.set('lng', '-73.54851007461548')
            params.set('radius', '50')
            params.set('units', 'km')
            params.set('imageText', 'true')

            if (lang) {
              params.set('lang', lang)
            }

            this.observable = this.http.get(environment.apiBaseUrl + '/api/v1/movies', { search: params, headers: this.headers })
                .map((response: Response) => {
                    const json = response.json()
                    let movies: Movie[] = []

                    if (json.movies) {
                      movies = this.parseMovies(json.movies)
                    }

                    return movies
                })
                .map((response: Movie[]) => {
                  return response.filter((movie: Movie) => movie.genres.length > 0)
                })
                .publishReplay(1)
                .refCount()
                .catch((error: any) => Observable.throw(error))

        }

        return this.observable
    }

    public getMovie(id: number, numDays: number = 7): Observable<Movie> {

        const params = new URLSearchParams()
        params.set('startDate', moment().format())
        params.set('numDays', numDays.toString())
        params.set('lat', '45.55799439258627')
        params.set('lng', '-73.54851007461548')
        params.set('radius', '50')
        params.set('units', 'km')
        params.set('imageText', 'true')
        params.set('api_key', '9h6jvc6de4ez8xwy53khs5bw')

        return this.http.get(environment.apiBaseUrl + `/movies/${id}/showings`, { search: params, headers: this.headers })
            .map((response: Response) => {
                const json = response.json()
                const movie = new Movie()

                for (const item of json) {
                    movie.deserialize(item)
                }

                console.log(json)
                return movie
            })
    }

    public getMovieInterests(uid: string): Observable<Movie[]> {
      const interestsRef = this.firebaseRef.child(`/users/${uid}/interestedMovies`)
      return this.db.list(interestsRef)
        .map((result: any[]) => {
          // Return array of movie id if the movie is not expired
          return result
            .filter((item: any) => item.$value)
            .map((item: any) => item.$key)
        })
        .mergeMap((result: string[]) => {
          // Retrieve movie data from database
          if (result.length === 0) {
            return Observable.of([])
          }

          const observableBatch: Observable<any>[] = []

          for (const movieId of result) {
            const movieRef = this.firebaseRef.child(`movies/${movieId}`)
            const observable =  this.db.object(movieRef).take(1)
            observableBatch.push(observable)
          }

          return Observable.forkJoin(observableBatch)
        })
        .map((movies: any[]) => movies.map((movie: any) => this.mapToMovie(movie)))
        .do(a => console.log(a))
        // .map((result: any[]) => {
        //   return result.map((item: any) => {
        //     const movie = new Movie()
        //     return movie.deserialize(item)
        //   })
        // })
    }

    public getTheatre(id: number): Observable<Theatre> {
      const params = new URLSearchParams()
      params.set('lat', '45.55799439258627')
      params.set('lng', '-73.54851007461548')
      params.set('units', 'km')
      params.set('api_key', '9h6jvc6de4ez8xwy53khs5bw')

      return this.http.get(environment.apiBaseUrl + `/theatres/${id}`, { search: params, headers: this.headers})
        .map((response: Response) => {
          const json = response.json()

          const theatre = new Theatre()
          theatre.id = json.theatreId
          theatre.name = json.name
          theatre.distance = json.location.distance

          return theatre
        })
    }

    public getTrailer(movieId: number): Observable<Trailer> {
        const quality: number[] = [0, 449, 457, 460, 461, 472]

        const params = new URLSearchParams()
        params.set('rootIds', movieId.toString())
        params.set('bitrateIds', quality.toString())
        params.set('trailersOnly', '1')
        params.set('languageid', '52')
        params.set('player_url', '0')
        params.set('best_eclip', '1')
        params.set('api_key', '9h6jvc6de4ez8xwy53khs5bw')

        return this.http.get(environment.apiBaseUrl + `/movies/${movieId}/trailers`, { search: params, headers: this.headers})
            .map((response: Response) => {
                const json = response.json()

                const item = json.response.trailers[0]

                if (item) {
                    const trailer = new Trailer()
                    trailer.deserialize(item)

                    return trailer
                } else {
                    return null
                }
            })
            // .do(a => console.log(a))
    }

    private parseMovies(json: any): Movie[] {
        const groupMovies = this.groupBy(json, 'rootId')

        const movies: Movie[] = []

        for (const rootId in groupMovies) {
            if (groupMovies.hasOwnProperty(rootId)) {
                const movie: Movie = new Movie()

                for (const movieResponse of groupMovies[rootId]) {
                    movie.deserialize(movieResponse)
                }

                movies.push(movie)
            }
        }

        movies.sort((a: Movie, b: Movie) => {
            const titleA = a.getTitle('en').toLowerCase()
            const titleB = b.getTitle('en').toLowerCase()
            return (titleA < titleB) ? -1 : (titleA > titleB) ? 1 : 0
        })

        return movies
    }

    public getTheatres(): Observable<Theatre[]> {
        const params = new URLSearchParams()
        params.set('lat', '45.55799439258627')
        params.set('lng', '-73.54851007461548')
        params.set('radius', '10')
        params.set('units', 'km')
        params.set('api_key', '9h6jvc6de4ez8xwy53khs5bw')

        return this.http.get(environment.apiBaseUrl + '/theatres', { search: params, headers: this.headers})
            .map((response: Response) => {
                let theatres: Theatre[] = []
                const json = response.json()

                for (const item of json) {
                    const theatre: Theatre = {
                        id: item.theatreId,
                        name: item.name,
                        distance: item.location.distance
                    }

                    theatres.push(theatre)
                }

                theatres = theatres.sort((a: Theatre, b: Theatre) => {
                    return a.distance - b.distance
                })

                return theatres
            })
            .catch((error: any) => Observable.throw(error))
    }

    public getShowtimesbyTheatre(theatreId: number): Observable<Movie[]> {
        const params = new URLSearchParams()
        params.set('startDate', moment().format('YYYY-MM-DD'))
        params.set('numDays', '7')
        params.set('imageText', 'true')
        params.set('api_key', '9h6jvc6de4ez8xwy53khs5bw')

        return this.http.get(environment.apiBaseUrl + `/theatres/${theatreId}/showings`, { search: params, headers: this.headers})
            .do((response) => console.log(response.json()))
            .map((response: Response) => {
                const json = response.json()
                const movies: Movie[] = this.parseMovies(json)

                for (let i = 0 ; i < movies.length ; i++) {
                  movies[i].showtimesByTheatre[0].theatre.id = theatreId
                }

                return movies
            })
    }

    private groupBy(xs: any[], key: string): any[] {
      return xs.reduce(function(rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x)
        return rv
      }, {})
    }

    private mapToMovie(data: any): Movie {
      console.log(data)
      const movie = new Movie()
      movie.rootId = data.$key
      movie.bannerUrls = data.banner
      movie.titles = data.title
      movie.rating = data.rating
      movie.duration = data.runtime

      return movie
    }
}



// WEBPACK FOOTER //
// C:/Users/florian.gadal/Workspace/cinenow-web/src/app/movies/shared/movie.service.ts